import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CRMLayout } from "@/components/CRMLayout";
import ProjectTabs from "@/components/ProjectTabs";
import { useAuth } from "@/App";
import { ArrowLeft, Brain, GitBranch, GitCommit, Github, Loader2 } from "lucide-react";
import { Detail, SOURCES, STATUTS, depuis } from "./SessionsPage";
import { AjoutSouvenir, Souvenir, useMemoire } from "./MemoirePage";
import ReprisePanneau from "@/components/ReprisePanneau";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Activité IA d'un projet : tout le travail fait dessus (Orca, GitHub, MCP, agents
 * NovaForge), son dépôt, et la mémoire propre au projet que lisent les agents.
 */
export default function ProjectActivitePage() {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const [projet, setProjet] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [ouverte, setOuverte] = useState(null);
  const { elements, charger, maj, supprimer } = useMemoire(token, { portee: "projet", cible: projectId });

  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    fetch(`${API_URL}/api/projects/${projectId}`, h).then((r) => r.json()).then(setProjet).catch(() => {});
    fetch(`${API_URL}/api/agent/sync/sessions?project_id=${projectId}&jours=120`, h)
      .then((r) => r.json()).then((d) => setSessions(Array.isArray(d) ? d : [])).catch(() => setSessions([]));
  }, [projectId, token]);

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <Link to="/dashboard/projects" className="mb-4 inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600">
          <ArrowLeft className="h-4 w-4" /> Projets
        </Link>
        <ProjectTabs projectId={projectId} actif="activite" />
        <h1 className="text-2xl font-bold text-slate-900">{projet?.name || "Projet"}</h1>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          {projet?.github_url && (
            <a href={projet.github_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700 hover:border-indigo-300">
              <Github className="h-4 w-4" /> {projet.github_url.replace("https://github.com/", "")}
            </a>
          )}
          {projet?.git_branch && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 font-mono text-xs"><GitBranch className="h-3.5 w-3.5" />{projet.git_branch}</span>
          )}
          {projet?.last_commit && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <GitCommit className="h-3.5 w-3.5" /> {projet.last_commit.sha} · {projet.last_commit.message} · {depuis(projet.last_commit.date)}
            </span>
          )}
          {!projet?.github_url && projet && <span className="text-xs text-slate-400">Aucun dépôt GitHub relié</span>}
        </div>

        {user?.role === "admin" && projet && (
          <div className="mt-5"><ReprisePanneau projetId={projectId} nom={projet.name} token={token} /></div>
        )}

        <div className="mt-6 grid gap-6 xl:grid-cols-[3fr_2fr]">
          <section>
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Sessions de travail</h2>
            {!sessions ? <Loader2 className="mt-4 h-5 w-5 animate-spin text-slate-400" /> : !sessions.length ? (
              <p className="mt-3 text-sm text-slate-500">
                Aucune session pour l'instant. Elles apparaissent seules dès que tu travailles sur ce projet
                (Orca, GitHub, chat Claude ou équipe IA).
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                {sessions.map((s) => {
                  const src = SOURCES[s.source] || SOURCES.autre;
                  const Icone = src.icon;
                  return (
                    <button key={s.id} onClick={() => setOuverte(s.id)}
                      className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-indigo-300">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${src.cls}`}><Icone className="h-3.5 w-3.5" />{s.machine || src.label}</span>
                        <span className={`rounded-full px-2 py-0.5 ${STATUTS[s.status]?.cls}`}>{STATUTS[s.status]?.label}</span>
                        <span className="ml-auto text-slate-400">{depuis(s.updated_at)}</span>
                      </div>
                      <p className="mt-1.5 line-clamp-1 text-sm font-medium text-slate-900">{s.title || "Session"}</p>
                      {s.summary && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{s.summary}</p>}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section>
            <h2 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-slate-500">
              <Brain className="h-4 w-4" /> Mémoire du projet
            </h2>
            <p className="mt-1 text-xs text-slate-500">Lue par tous les agents qui travaillent sur ce projet.</p>
            <div className="mt-3"><AjoutSouvenir token={token} portee="projet" cible={projectId} onAjout={charger} /></div>
            <div className="mt-3 space-y-2">
              {(elements || []).map((m) => <Souvenir key={m.id} m={m} onMaj={maj} onSupprimer={supprimer} />)}
            </div>
          </section>
        </div>
      </div>
      {ouverte && <Detail id={ouverte} token={token} onClose={() => setOuverte(null)} />}
    </CRMLayout>
  );
}
